import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import {
  Search, Filter, Plus, ChevronRight, Shield, AlertTriangle,
  CheckCircle, Clock, UserCheck, FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700",
  gathering: "bg-blue-100 text-blue-700",
  analyzing: "bg-yellow-100 text-yellow-700",
  qa_review: "bg-purple-100 text-purple-700",
  classified: "bg-green-100 text-green-700",
};

const STATUS_ICONS = {
  draft: Clock,
  gathering: Search,
  analyzing: AlertTriangle,
  qa_review: UserCheck,
  classified: CheckCircle,
};

const CLASSIFICATION_BADGES = {
  trusted: "bg-green-100 text-green-700 border-green-300",
  limited_trust: "bg-yellow-100 text-yellow-700 border-yellow-300",
  unverified: "bg-orange-100 text-orange-700 border-orange-300",
  flagged: "bg-red-100 text-red-700 border-red-300",
};

export default function VettingDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function loadCases() {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/vetting/cases`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setCases(res.data.cases || []);
    } catch (e) {
      console.error("Failed to load vetting cases:", e);
    } finally {
      setLoading(false);
    }
  }

  const filtered = cases.filter(c =>
    !searchTerm || c.subject_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed'] tracking-wide">
            VETTING CASES
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage and monitor vetting operations</p>
        </div>
        <button
          onClick={() => navigate("/vetting/new")}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Case
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by subject name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="gathering">Gathering</option>
            <option value="analyzing">Analyzing</option>
            <option value="qa_review">QA Review</option>
            <option value="classified">Classified</option>
          </select>
        </div>
      </div>

      {/* Case list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No cases found</p>
          <p className="text-sm mt-1">Create a new case to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-app border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Case #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Subject</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Classification</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const StatusIcon = STATUS_ICONS[c.status] || Clock;
                return (
                  <tr
                    key={c.id}
                    className="border-b border-slate-100 hover:bg-app cursor-pointer transition-colors"
                    onClick={() => navigate(`/vetting/${c.id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{c.case_number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{c.subject_name}</p>
                      <p className="text-xs text-slate-400">{c.subject_id_type}: {c.subject_id_value}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-600"}`}>
                        <StatusIcon className="w-3 h-3" />
                        {c.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.classification ? (
                        <span className={`inline-block px-2 py-1 rounded border text-xs font-medium ${CLASSIFICATION_BADGES[c.classification] || ""}`}>
                          {c.classification?.replace(/_/g, " ")}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
